import React, { useState, useEffect } from 'react';
import api from '../utils/api';

const TeamManagementPanel = () => {
  const [teams, setTeams] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  
  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddMembersModal, setShowAddMembersModal] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);

  // Form state
  const [createForm, setCreateForm] = useState({ teamName: '', leaderId: null });
  const [editForm, setEditForm] = useState({ teamName: '' });
  const [selectedMembers, setSelectedMembers] = useState([]);

  // Fetch teams and available users
  const fetchTeams = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/teams');
      setTeams(response.data.data);
      setMessage(null);
    } catch (error) {
      console.error('Error fetching teams:', error);
      setMessage({ type: 'error', text: 'Failed to load teams' });
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableUsers = async () => {
    try {
      const response = await api.get('/admin/teams/available-users');
      setAvailableUsers(response.data.data);
    } catch (error) {
      console.error('Error fetching available users:', error);
    }
  };

  useEffect(() => {
    fetchTeams();
    fetchAvailableUsers();
  }, []);

  // Create team
  const handleCreateTeam = async () => {
    if (!createForm.teamName || !createForm.leaderId) {
      setMessage({ type: 'error', text: 'Please fill in all fields' });
      return;
    }

    try {
      await api.post('/admin/teams', {
        teamName: createForm.teamName,
        leaderId: parseInt(createForm.leaderId)
      });

      setMessage({ type: 'success', text: 'Team created successfully' });
      setShowCreateModal(false);
      setCreateForm({ teamName: '', leaderId: null });
      fetchTeams();
      fetchAvailableUsers();
    } catch (error) {
      console.error('Error creating team:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to create team'
      });
    }
  };

  // Update team name
  const handleUpdateTeam = async () => {
    if (!editForm.teamName) {
      setMessage({ type: 'error', text: 'Team name is required' });
      return;
    }

    try {
      await api.put(`/admin/teams/${selectedTeam.id}`, { teamName: editForm.teamName });

      setMessage({ type: 'success', text: 'Team updated successfully' });
      setShowEditModal(false);
      setEditForm({ teamName: '' });
      fetchTeams();
    } catch (error) {
      console.error('Error updating team:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to update team'
      });
    }
  };

  // Add members to team
  const handleAddMembers = async () => {
    if (selectedMembers.length === 0) {
      setMessage({ type: 'error', text: 'Please select at least one member' });
      return;
    }

    try {
      await api.post(`/admin/teams/${selectedTeam.id}/members`, {
        userIds: selectedMembers.map(id => parseInt(id))
      });

      setMessage({ type: 'success', text: 'Members added successfully' });
      setShowAddMembersModal(false);
      setSelectedMembers([]);
      fetchTeams();
      fetchAvailableUsers();
    } catch (error) {
      console.error('Error adding members:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to add members'
      });
    }
  };

  // Remove member from team
  const handleRemoveMember = async (memberId) => {
    if (!window.confirm('Remove this member from the team?')) {
      return;
    }

    try {
      await api.delete(`/admin/teams/${selectedTeam.id}/members/${memberId}`);

      setMessage({ type: 'success', text: 'Member removed successfully' });
      fetchTeams();
      fetchAvailableUsers();
    } catch (error) {
      console.error('Error removing member:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to remove member'
      });
    }
  };

  // Delete team
  const handleDeleteTeam = async (team) => {
    if (!window.confirm(`Delete team "${team.team_name}"? Members will be released.`)) {
      return;
    }

    try {
      await api.delete(`/admin/teams/${team.id}`);

      setMessage({ type: 'success', text: 'Team deleted successfully' });
      fetchTeams();
      fetchAvailableUsers();
    } catch (error) {
      console.error('Error deleting team:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to delete team'
      });
    }
  };

  // Modal handlers
  const openEditModal = (team) => {
    setSelectedTeam(team);
    setEditForm({ teamName: team.team_name });
    setShowEditModal(true);
  };

  const openAddMembersModal = async (team) => {
    setSelectedTeam(team);
    setSelectedMembers([]);
    setShowAddMembersModal(true);
  };

  const openViewTeamModal = async (team) => {
    try {
      const response = await api.get(`/admin/teams/${team.id}`);
      setSelectedTeam(response.data.data);
    } catch (error) {
      console.error('Error fetching team details:', error);
      setMessage({ type: 'error', text: 'Failed to load team details' });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading teams...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Message */}
      {message && (
        <div
          className={`p-4 rounded-lg ${
            message.type === 'success'
              ? 'bg-green-100 text-green-800'
              : 'bg-red-100 text-red-800'
          }`}
        >
          {message.text}
        </div>
      )}

      {/* Header */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900">Team Management</h3>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          + Create Team
        </button>
      </div>

      {/* Teams Grid */}
      {teams.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No teams created yet. Create your first team above.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {teams.map(team => (
            <div
              key={team.id}
              className="border border-gray-200 rounded-lg p-4 hover:shadow-lg transition"
            >
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h4 className="font-semibold text-gray-900">{team.team_name}</h4>
                  <p className="text-sm text-gray-600">
                    Leader: {team.leader_name} ({team.leader_email})
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => openEditModal(team)}
                    className="text-blue-600 hover:text-blue-800 text-sm"
                    title="Edit team"
                  >
                    ✏️
                  </button>
                  <button
                    onClick={() => handleDeleteTeam(team)}
                    className="text-red-600 hover:text-red-800 text-sm"
                    title="Delete team"
                  >
                    🗑️
                  </button>
                </div>
              </div>

              <div className="mb-3">
                <p className="text-sm text-gray-600">
                  {team.member_count} member{team.member_count !== 1 ? 's' : ''}
                </p>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => openViewTeamModal(team)}
                  className="flex-1 px-3 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 text-sm"
                >
                  View Members
                </button>
                <button
                  onClick={() => openAddMembersModal(team)}
                  className="flex-1 px-3 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200 text-sm"
                >
                  Add Members
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Team Modal */}
      {showCreateModal && (
        <Modal onClose={() => setShowCreateModal(false)}>
          <h3 className="text-lg font-semibold mb-4">Create New Team</h3>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Team Name
              </label>
              <input
                type="text"
                value={createForm.teamName}
                onChange={e => setCreateForm({ ...createForm, teamName: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                placeholder="e.g., Sales Team"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Team Leader
              </label>
              <select
                value={createForm.leaderId || ''}
                onChange={e => setCreateForm({ ...createForm, leaderId: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
              >
                <option value="">Select a leader...</option>
                {availableUsers
                  .filter(u => u.role === 'team_leader' || u.role === 'team_member')
                  .map(user => (
                    <option key={user.id} value={user.id}>
                      {user.full_name} ({user.email})
                    </option>
                  ))}
              </select>
            </div>
          </div>

          <div className="flex gap-3 mt-6">
            <button
              onClick={() => setShowCreateModal(false)}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleCreateTeam}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Create Team
            </button>
          </div>
        </Modal>
      )}

      {/* Edit Team Modal */}
      {showEditModal && (
        <Modal onClose={() => setShowEditModal(false)}>
          <h3 className="text-lg font-semibold mb-4">Edit Team</h3>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Team Name
            </label>
            <input
              type="text"
              value={editForm.teamName}
              onChange={e => setEditForm({ ...editForm, teamName: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
            />
          </div>

          <div className="flex gap-3 mt-6">
            <button
              onClick={() => setShowEditModal(false)}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleUpdateTeam}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Save Changes
            </button>
          </div>
        </Modal>
      )}

      {/* Add Members Modal */}
      {showAddMembersModal && (
        <Modal onClose={() => setShowAddMembersModal(false)}>
          <h3 className="text-lg font-semibold mb-4">
            Add Members to {selectedTeam?.team_name}
          </h3>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Members
            </label>
            <div className="space-y-2 max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-3">
              {availableUsers.length === 0 ? (
                <p className="text-gray-500 text-sm">No available users to add</p>
              ) : (
                availableUsers.map(user => (
                  <label key={user.id} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      value={user.id}
                      checked={selectedMembers.includes(user.id.toString())}
                      onChange={e => {
                        if (e.target.checked) {
                          setSelectedMembers([...selectedMembers, user.id.toString()]);
                        } else {
                          setSelectedMembers(
                            selectedMembers.filter(id => id !== user.id.toString())
                          );
                        }
                      }}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">
                      {user.full_name} ({user.email})
                    </span>
                  </label>
                ))
              )}
            </div>
          </div>

          <div className="flex gap-3 mt-6">
            <button
              onClick={() => setShowAddMembersModal(false)}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleAddMembers}
              disabled={selectedMembers.length === 0}
              className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              Add {selectedMembers.length} Member{selectedMembers.length !== 1 ? 's' : ''}
            </button>
          </div>
        </Modal>
      )}

      {/* View Team Members Modal */}
      {selectedTeam && selectedTeam.members && (
        <Modal
          onClose={() => {
            setSelectedTeam(null);
            fetchTeams();
          }}
        >
          <h3 className="text-lg font-semibold mb-4">
            Team: {selectedTeam.team_name}
          </h3>

          <div className="space-y-3">
            {selectedTeam.members.length === 0 ? (
              <p className="text-gray-500 text-sm">No members in this team</p>
            ) : (
              selectedTeam.members.map(member => (
                <div
                  key={member.id}
                  className="flex justify-between items-center p-3 bg-gray-50 rounded-lg"
                >
                  <div>
                    <p className="font-medium text-gray-900">{member.full_name}</p>
                    <p className="text-sm text-gray-600">{member.email}</p>
                    {member.is_leader && (
                      <span className="inline-block mt-1 px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">
                        Team Leader
                      </span>
                    )}
                  </div>
                  {!member.is_leader && (
                    <button
                      onClick={() => handleRemoveMember(member.id)}
                      className="text-red-600 hover:text-red-800"
                      title="Remove member"
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))
            )}
          </div>

          <div className="mt-6">
            <button
              onClick={() => {
                setSelectedTeam(null);
                fetchTeams();
              }}
              className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
            >
              Close
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

// Reusable Modal Component
const Modal = ({ onClose, children }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto p-6">
        {children}
      </div>
    </div>
  );
};

export default TeamManagementPanel;
